const kStoragePrefix = 'paxton.outlook.attachment-files.v1'

/** The only scope in which an uploaded copy of an attachment may be reused. */
export type OutlookAttachmentFileScope = {
  /** Signed-in Paxton user. */
  userEmail: string
  mailboxEmail: string
  messageId: string
  matterId: string
}

function getStorageKey(scope: OutlookAttachmentFileScope): string {
  return `${kStoragePrefix}.${JSON.stringify([scope.userEmail.trim().toLowerCase(), scope.mailboxEmail.trim().toLowerCase(), scope.messageId, scope.matterId])}`
}

/**
 * Outlook attachment id -> Matter file id, for attachments this user already uploaded from this
 * email into this matter. The link is only a local hint: the caller must confirm through the API
 * that the file still exists in the matter before reusing it.
 */
export function readOutlookAttachmentFileLinks(scope: OutlookAttachmentFileScope, storage: Storage = window.localStorage): Record<string, string> {
  try {
    const value = JSON.parse(storage.getItem(getStorageKey(scope)) ?? 'null') as unknown
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string'))
  } catch {
    return {}
  }
}

export function writeOutlookAttachmentFileLink(
  scope: OutlookAttachmentFileScope,
  attachmentId: string,
  fileId: string,
  storage: Storage = window.localStorage
): void {
  storeLinks(scope, { ...readOutlookAttachmentFileLinks(scope, storage), [attachmentId]: fileId }, storage)
}

export function removeOutlookAttachmentFileLink(scope: OutlookAttachmentFileScope, attachmentId: string, storage: Storage = window.localStorage): void {
  const { [attachmentId]: _removed, ...links } = readOutlookAttachmentFileLinks(scope, storage)
  storeLinks(scope, links, storage)
}

export function clearOutlookAttachmentFileLinks(): void {
  try {
    for (let index = window.localStorage.length - 1; index >= 0; index--) {
      const key = window.localStorage.key(index)
      if (key?.startsWith(`${kStoragePrefix}.`)) window.localStorage.removeItem(key)
    }
  } catch {
    // Optional browser storage must not prevent Firebase sign-out.
  }
}

function storeLinks(scope: OutlookAttachmentFileScope, links: Record<string, string>, storage: Storage): void {
  try {
    if (Object.keys(links).length === 0) storage.removeItem(getStorageKey(scope))
    else storage.setItem(getStorageKey(scope), JSON.stringify(links))
  } catch {
    // Reuse is an optimization. Without the link the attachment is uploaded again next time.
  }
}
